import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { catchError, forkJoin, map, of } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  AddInsuranceMemberCertificatePayload,
  CreateInsuranceBulkChangeBatchPayload,
  CreateInsuranceMasterPolicyPayload,
  InsuranceApiService,
  InsuranceCensusReport,
  InsuranceMasterPolicyDetail,
  InsurancePolicyListItem,
  InsuranceProductDetail,
  UpdateInsuranceMasterPolicyPayload
} from './insurance-api.service';
import {
  INSURANCE_CENSUS_REPORT_FIELDS,
  INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS,
  InsuranceBulkChangeRowDraft,
  applyBulkRowScalarPayload,
  bulkRowScalarPayloadJson,
  censusReportPayloadJson,
  emptyBulkRowDraft,
  serializeBulkRowsJson
} from './insurance-group-schemas';

type MasterPolicyTab = 'contract' | 'members' | 'bulk' | 'billing' | 'reports';

@Component({
  standalone: true,
  selector: 'ain-insurance-master-policy-detail',
  imports: [
    AinDatePipe,
    JalaliDateInputComponent,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    SelectModule,
    TableModule,
    PartySelectorComponent,
    ButtonModule,
    DialogModule,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.group.masterPolicyDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.masterPolicies.help.detail.title'"
        [descriptionKey]="'insurance.masterPolicies.help.detail.description'"
        [bulletKeys]="[
          'insurance.masterPolicies.help.detail.bullets.tabs',
          'insurance.masterPolicies.help.detail.bullets.contract',
          'insurance.masterPolicies.help.detail.bullets.delete'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/master-policies']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.group.masterPolicies.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.masterPolicies.help.info.title'"
              [descriptionKey]="'insurance.masterPolicies.help.info.description'"
              [bulletKeys]="[
                'insurance.masterPolicies.help.info.bullets.employer',
                'insurance.masterPolicies.help.info.bullets.product',
                'insurance.masterPolicies.help.info.bullets.dates'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.legalEntityId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.masterPolicyNumber') }}</span>
              <input pInputText [(ngModel)]="createForm.masterPolicyNumber" name="masterPolicyNumber" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.employerPartyId') }}</span>
              <ain-party-selector [(ngModel)]="createForm.employerPartyId" name="employerPartyId" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.productVersionId') }}</span>
              <p-select
                [options]="productVersionOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.productVersionId"
                name="productVersionId"
                [filter]="true"
                [showClear]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.billingMode') }}</span>
              <p-select
                [options]="billingModeOptions"
                optionValue="value"
                [(ngModel)]="createForm.billingMode"
                name="createBillingMode"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.effectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveFrom" name="effectiveFrom" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicies.fields.effectiveTo') }}</span>
              <ain-date-input [(ngModel)]="createForm.effectiveTo" name="effectiveTo" />
            </label>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!item()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/master-policies']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveUpdate()"></button>
            <button pButton type="button" severity="danger" [label]="t('common.delete')" [disabled]="saving()" (click)="deleteMasterPolicy()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.group.masterPolicies.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.masterPolicies.help.info.title'"
              [descriptionKey]="'insurance.masterPolicies.help.info.description'"
              [bulletKeys]="[
                'insurance.masterPolicies.help.info.bullets.employer',
                'insurance.masterPolicies.help.info.bullets.product',
                'insurance.masterPolicies.help.info.bullets.dates'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.group.masterPolicies.fields.masterPolicyNumber') }}: <strong>{{ item()!.masterPolicyNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(item()!.status)) }}</strong></span>
            <span>{{ t('insurance.group.masterPolicies.fields.billingMode') }}: <strong>{{ t(billingModeKey(item()!.billingMode)) }}</strong></span>
            <span>{{ t('insurance.group.masterPolicies.fields.activeMemberCount') }}: <strong>{{ activeMembersCount() }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'contract'" (click)="activeTab.set('contract')">{{ t('insurance.group.masterPolicyDetail.tabs.contract') }}</button>
            <button type="button" [class.active]="activeTab() === 'members'" (click)="activeTab.set('members')">{{ t('insurance.group.masterPolicyDetail.tabs.members') }}</button>
            <button type="button" [class.active]="activeTab() === 'bulk'" (click)="activeTab.set('bulk')">{{ t('insurance.group.masterPolicyDetail.tabs.bulk') }}</button>
            <button type="button" [class.active]="activeTab() === 'billing'" (click)="activeTab.set('billing')">{{ t('insurance.group.masterPolicyDetail.tabs.billing') }}</button>
            <button type="button" [class.active]="activeTab() === 'reports'" (click)="activeTab.set('reports')">{{ t('insurance.group.masterPolicyDetail.tabs.reports') }}</button>
          </div>

          @if (activeTab() === 'contract') {
            <div class="lines-header">
              <h4>{{ t('insurance.group.masterPolicyDetail.tabs.contract') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.masterPolicies.help.tabs.contract.title'"
                [descriptionKey]="'insurance.masterPolicies.help.tabs.contract.description'"
                [bulletKeys]="[
                  'insurance.masterPolicies.help.tabs.contract.bullets.billing',
                  'insurance.masterPolicies.help.tabs.contract.bullets.dates'
                ]" />
            </div>
            <form class="grid">
              <label>
                <span>{{ t('insurance.group.masterPolicies.fields.employerPartyId') }}</span>
                <ain-party-selector [(ngModel)]="updateForm.employerPartyId" name="updateEmployerPartyId" />
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.fields.productVersionId') }}</span>
                <p-select
                  [options]="productVersionOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="updateForm.productVersionId"
                  name="updateProductVersionId"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body" />
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.fields.billingMode') }}</span>
                <p-select
                  [options]="billingModeOptions"
                  optionValue="value"
                  [(ngModel)]="updateForm.billingMode"
                  name="updateBillingMode"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.fields.effectiveFrom') }}</span>
                <ain-date-input [(ngModel)]="updateForm.effectiveFrom" name="updateEffectiveFrom" />
              </label>
              <label>
                <span>{{ t('insurance.group.masterPolicies.fields.effectiveTo') }}</span>
                <ain-date-input [(ngModel)]="updateForm.effectiveTo" name="updateEffectiveTo" />
              </label>
            </form>
          } @else if (activeTab() === 'members') {
            <div class="lines-header">
              <h4>{{ t('insurance.group.masterPolicyDetail.tabs.members') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.masterPolicies.help.tabs.members.title'"
                  [descriptionKey]="'insurance.masterPolicies.help.tabs.members.description'"
                  [bulletKeys]="[
                    'insurance.masterPolicies.help.tabs.members.bullets.certificate',
                    'insurance.masterPolicies.help.tabs.members.bullets.coverage'
                  ]" />
                <button pButton type="button" [label]="t('insurance.group.masterPolicyDetail.members.add')" (click)="openMemberDialog()" [disabled]="saving()"></button>
              </div>
            </div>
            <p-table [value]="item()!.members" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.group.masterPolicyDetail.members.fields.certificateNumber') }}</th>
                  <th>{{ t('insurance.group.masterPolicyDetail.members.fields.memberPartyId') }}</th>
                  <th>{{ t('insurance.group.masterPolicies.fields.status') }}</th>
                  <th>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveFrom') }}</th>
                  <th>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveTo') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.certificateNumber }}</td>
                  <td>{{ row.memberPartyId }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.coverageEffectiveFrom | ainDate }}</td>
                  <td>{{ row.coverageEffectiveTo | ainDate }}</td>
                </tr>
              </ng-template>
            </p-table>
          } @else if (activeTab() === 'bulk') {
            <div class="lines-header">
              <h4>{{ t('insurance.group.masterPolicyDetail.tabs.bulk') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.masterPolicies.help.tabs.bulk.title'"
                  [descriptionKey]="'insurance.masterPolicies.help.tabs.bulk.description'"
                  [bulletKeys]="[
                    'insurance.masterPolicies.help.tabs.bulk.bullets.approve',
                    'insurance.masterPolicies.help.tabs.bulk.bullets.apply'
                  ]" />
                <button pButton type="button" [label]="t('insurance.group.masterPolicyDetail.bulk.create')" (click)="openBulkDialog()" [disabled]="saving()"></button>
              </div>
            </div>
            <p-table [value]="item()!.bulkChanges" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.group.masterPolicyDetail.bulk.fields.changeType') }}</th>
                  <th>{{ t('insurance.group.masterPolicies.fields.status') }}</th>
                  <th>{{ t('insurance.group.masterPolicyDetail.bulk.fields.processedCount') }}</th>
                  <th>{{ t('insurance.group.masterPolicyDetail.bulk.fields.createdAt') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ t(bulkChangeTypeKey(row.changeType)) }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.processedCount }}</td>
                  <td>{{ row.createdAt | ainDate }}</td>
                  <td class="actions-inline">
                    <button pButton type="button" size="small" severity="secondary" [label]="t('insurance.group.masterPolicyDetail.bulk.approve')" [disabled]="row.status !== 'PendingApproval' || saving()" (click)="approveBulk(row.id)"></button>
                    <button pButton type="button" size="small" [label]="t('insurance.group.masterPolicyDetail.bulk.apply')" [disabled]="row.status !== 'Approved' || saving()" (click)="applyBulk(row.id)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          } @else if (activeTab() === 'billing') {
            <div class="lines-header">
              <h4>{{ t('insurance.group.masterPolicyDetail.tabs.billing') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.masterPolicies.help.tabs.billing.title'"
                [descriptionKey]="'insurance.masterPolicies.help.tabs.billing.description'"
                [bulletKeys]="[
                  'insurance.masterPolicies.help.tabs.billing.bullets.prorata'
                ]" />
            </div>
            <p>{{ t('insurance.group.masterPolicyDetail.billing.note') }}</p>
            <p><strong>{{ t('insurance.group.masterPolicyDetail.billing.latestTerminationAdjustment') }}:</strong> {{ item()!.latestTerminationProRataAdjustment ?? 0 }}</p>
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.group.masterPolicyDetail.tabs.reports') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.masterPolicies.help.tabs.reports.title'"
                  [descriptionKey]="'insurance.masterPolicies.help.tabs.reports.description'"
                  [bulletKeys]="[
                    'insurance.masterPolicies.help.tabs.reports.bullets.census'
                  ]" />
                <button pButton type="button" severity="secondary" [label]="t('insurance.group.masterPolicyDetail.reports.refresh')" (click)="load()" [disabled]="saving()"></button>
              </div>
            </div>
            @if (!censusReport()) {
              <p>{{ t('insurance.group.masterPolicyDetail.reports.empty') }}</p>
            } @else {
              <ain-structured-json-display
                [fields]="censusReportFields"
                [payloadJson]="censusReportPayloadJson()" />
              <p-table [value]="censusReport()!.members" dataKey="certificateId" styleClass="lines-table section-gap">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.group.masterPolicyDetail.members.fields.certificateNumber') }}</th>
                    <th>{{ t('insurance.group.masterPolicyDetail.members.fields.memberPartyId') }}</th>
                    <th>{{ t('insurance.group.masterPolicies.fields.status') }}</th>
                    <th>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveFrom') }}</th>
                    <th>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveTo') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.certificateNumber }}</td>
                    <td>{{ row.memberPartyId }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.coverageEffectiveFrom | ainDate }}</td>
                    <td>{{ row.coverageEffectiveTo | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }

        <p-dialog
          [header]="t('insurance.group.masterPolicyDetail.members.dialog.add')"
          [modal]="true"
          [(visible)]="memberDialogVisible"
          [style]="{ width: '720px' }">
          <div class="dialog-grid">
            <label class="wide">
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.memberPartyId') }}</span>
              <ain-party-selector [(ngModel)]="memberForm.memberPartyId" name="dialogMemberPartyId" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.certificateNumber') }}</span>
              <input pInputText [(ngModel)]="memberForm.certificateNumber" name="dialogCertificateNumber" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.planCode') }}</span>
              <input pInputText [(ngModel)]="memberForm.planCode" name="dialogPlanCode" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="memberForm.coverageEffectiveFrom" name="dialogCoverageEffectiveFrom" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveTo') }}</span>
              <ain-date-input [(ngModel)]="memberForm.coverageEffectiveTo" name="dialogCoverageEffectiveTo" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.childPolicyId') }}</span>
              <p-select
                [options]="policyOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="memberForm.childPolicyId"
                name="dialogChildPolicyId"
                [filter]="true"
                [showClear]="true"
                appendTo="body" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="memberDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !memberForm.memberPartyId || !memberForm.certificateNumber" (click)="addMember()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog
          [header]="t('insurance.group.masterPolicyDetail.bulk.dialog.create')"
          [modal]="true"
          [(visible)]="bulkDialogVisible"
          [style]="{ width: '820px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.bulk.fields.changeType') }}</span>
              <p-select
                [options]="bulkChangeTypeOptions"
                optionValue="value"
                [(ngModel)]="bulkForm.changeType"
                name="dialogBulkChangeType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label class="wide">
              <span>{{ t('insurance.group.masterPolicyDetail.bulk.fields.fileId') }}</span>
              <input #bulkFileInput type="file" hidden (change)="onBulkFileSelected($event)" />
              <div class="file-row">
                <button pButton type="button" severity="secondary" [label]="t('insurance.group.masterPolicyDetail.bulk.actions.chooseFile')" (click)="bulkFileInput.click()"></button>
                <span>{{ bulkFileName() || t('insurance.group.masterPolicyDetail.bulk.fields.fileNone') }}</span>
              </div>
              <p class="field-hint">{{ t('insurance.group.masterPolicyDetail.bulk.fields.fileHint') }}</p>
            </label>
          </div>
          <div class="lines-header section-gap">
            <h4>{{ t('insurance.group.masterPolicyDetail.bulk.fields.rows') }}</h4>
            <button pButton type="button" size="small" [label]="t('insurance.group.masterPolicyDetail.bulk.dialog.addRow')" (click)="openBulkRowDialog()"></button>
          </div>
          @if (bulkRowDrafts().length === 0) {
            <p>{{ t('insurance.group.masterPolicyDetail.bulk.states.noRows') }}</p>
          } @else {
            <p-table [value]="bulkRowDrafts()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.group.schemas.bulkRow.rowKey') }}</th>
                  <th>{{ t('insurance.group.schemas.bulkRow.memberPartyId') }}</th>
                  <th>{{ t('insurance.group.schemas.bulkRow.certificateNumber') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                <tr>
                  <td>{{ row.rowKey }}</td>
                  <td>{{ row.memberPartyId }}</td>
                  <td>{{ row.certificateNumber || '-' }}</td>
                  <td class="actions-inline">
                    <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="openBulkRowDialog(rowIndex)"></button>
                    <button pButton type="button" size="small" severity="danger" [label]="t('common.remove')" (click)="removeBulkRow(rowIndex)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="bulkDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('insurance.group.masterPolicyDetail.bulk.create')" [disabled]="saving() || bulkRowDrafts().length === 0" (click)="createBulkBatch()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog
          [header]="bulkRowDialogIndex() === null ? t('insurance.group.masterPolicyDetail.bulk.dialog.addRow') : t('common.edit')"
          [modal]="true"
          [(visible)]="bulkRowDialogVisible"
          [style]="{ width: '760px' }">
          <ain-structured-json-form
            [fields]="bulkRowScalarFields"
            [(payloadJson)]="bulkRowScalarPayloadJson" />
          <div class="dialog-grid section-gap">
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveFrom') }}</span>
              <ain-date-input [(ngModel)]="bulkRowDraft.coverageEffectiveFrom" name="bulkRowCoverageFrom" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.coverageEffectiveTo') }}</span>
              <ain-date-input [(ngModel)]="bulkRowDraft.coverageEffectiveTo" name="bulkRowCoverageTo" />
            </label>
            <label>
              <span>{{ t('insurance.group.masterPolicyDetail.bulk.fields.terminationDate') }}</span>
              <ain-date-input [(ngModel)]="bulkRowDraft.terminationDateUtc" name="bulkRowTerminationDate" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.group.masterPolicyDetail.members.fields.childPolicyId') }}</span>
              <p-select
                [options]="policyOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="bulkRowDraft.childPolicyId"
                name="bulkRowChildPolicyId"
                [filter]="true"
                [showClear]="true"
                appendTo="body" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="bulkRowDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveBulkRowDialog()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .actions-inline { display: flex; gap: 6px; justify-content: flex-end; }
    .file-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .field-hint { font-size: 12px; color: #64748b; margin: 4px 0 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceMasterPolicyDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly item = signal<InsuranceMasterPolicyDetail | null>(null);
  readonly activeTab = signal<MasterPolicyTab>('contract');
  readonly isCreateMode = signal(false);
  readonly error = signal('');
  readonly censusReport = signal<InsuranceCensusReport | null>(null);
  readonly productVersionOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly policies = signal<InsurancePolicyListItem[]>([]);
  readonly memberDialogVisible = signal(false);
  readonly bulkDialogVisible = signal(false);
  readonly bulkRowDialogVisible = signal(false);
  readonly bulkRowDialogIndex = signal<number | null>(null);
  readonly bulkRowDrafts = signal<InsuranceBulkChangeRowDraft[]>([]);
  readonly bulkFileName = signal('');
  readonly censusReportFields = INSURANCE_CENSUS_REPORT_FIELDS;
  readonly bulkRowScalarFields = INSURANCE_GROUP_BULK_ROW_SCALAR_FIELDS;

  createForm: CreateInsuranceMasterPolicyPayload = {
    legalEntityId: '',
    masterPolicyNumber: '',
    employerPartyId: '',
    productVersionId: '',
    billingMode: 'MasterBilled',
    effectiveFrom: new Date().toISOString(),
    effectiveTo: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString()
  };
  updateForm: UpdateInsuranceMasterPolicyPayload = {
    employerPartyId: '',
    productVersionId: '',
    billingMode: 'MasterBilled',
    effectiveFrom: new Date().toISOString(),
    effectiveTo: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString()
  };
  memberForm: AddInsuranceMemberCertificatePayload = {
    memberPartyId: '',
    certificateNumber: '',
    coverageEffectiveFrom: new Date().toISOString(),
    coverageEffectiveTo: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString(),
    childPolicyId: null,
    planCode: null
  };
  bulkForm: CreateInsuranceBulkChangeBatchPayload = {
    changeType: 'Enroll',
    fileId: null,
    rowsJson: '[]'
  };
  bulkRowDraft: InsuranceBulkChangeRowDraft = emptyBulkRowDraft(0);
  bulkRowScalarPayloadJson = '{}';

  readonly billingModeOptions = [
    { value: 'MasterBilled', labelKey: 'insurance.masterPolicies.billingMode.masterBilled' },
    { value: 'MemberBilled', labelKey: 'insurance.masterPolicies.billingMode.memberBilled' }
  ];

  readonly bulkChangeTypeOptions = [
    { value: 'Enroll', labelKey: 'insurance.masterPolicies.bulkChangeType.enroll' },
    { value: 'Terminate', labelKey: 'insurance.masterPolicies.bulkChangeType.terminate' },
    { value: 'Update', labelKey: 'insurance.masterPolicies.bulkChangeType.update' }
  ];

  ngOnInit(): void {
    this.loadProductVersionOptions();
    this.api.listPolicies().subscribe({
      next: rows => this.policies.set(rows),
      error: () => this.policies.set([])
    });

    const id = this.route.snapshot.paramMap.get('masterPolicyId');
    const isNew = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNew) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!id) {
      this.loading.set(false);
      return;
    }

    this.load();
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('masterPolicyId');
    if (!id) {
      return;
    }

    this.loading.set(true);
    this.api.getMasterPolicy(id).subscribe({
      next: item => {
        this.item.set(item);
        if (item) {
          this.updateForm = {
            employerPartyId: item.employerPartyId,
            productVersionId: item.productVersionId,
            billingMode: item.billingMode,
            effectiveFrom: item.effectiveFrom,
            effectiveTo: item.effectiveTo
          };
          this.api.getMasterPolicyCensusReport(item.id).subscribe(report => {
            this.censusReport.set(report);
          });
        }
        this.loading.set(false);
      },
      error: () => {
        this.item.set(null);
        this.loading.set(false);
      }
    });
  }

  loadProductVersionOptions(): void {
    this.api.listProducts().subscribe({
      next: products => {
        if (products.length === 0) {
          this.productVersionOptions.set([]);
          return;
        }
        forkJoin(
          products.map(product =>
            this.api.getProduct(product.id).pipe(catchErrorToNull())
          )
        ).subscribe(details => {
          const options = (details.filter(item => item !== null) as InsuranceProductDetail[])
            .flatMap(product =>
              product.versions.map(version => ({
                value: version.id,
                label: `${product.code} v${version.versionNumber} (${version.status})`
              }))
            );
          this.productVersionOptions.set(options);
        });
      },
      error: () => this.productVersionOptions.set([])
    });
  }

  policyOptions(): Array<{ label: string; value: string }> {
    return this.policies().map(policy => ({
      value: policy.id,
      label: `${policy.policyNumber} — ${policy.productName}`
    }));
  }

  censusReportPayloadJson(): string {
    const report = this.censusReport();
    return report
      ? censusReportPayloadJson(report)
      : '{}';
  }

  pageTitle(): string {
    if (this.isCreateMode()) {
      return this.t('insurance.group.masterPolicies.actions.create');
    }

    return this.item()?.masterPolicyNumber ?? this.t('insurance.group.masterPolicyDetail.title');
  }

  activeMembersCount(): number {
    return this.item()?.members.filter(member => member.status === 'Active').length ?? 0;
  }

  openMemberDialog(): void {
    this.memberForm = {
      memberPartyId: '',
      certificateNumber: '',
      coverageEffectiveFrom: new Date().toISOString(),
      coverageEffectiveTo: new Date(Date.now() + (365 * 24 * 60 * 60 * 1000)).toISOString(),
      childPolicyId: null,
      planCode: null
    };
    this.memberDialogVisible.set(true);
  }

  openBulkDialog(): void {
    this.bulkForm = {
      changeType: 'Enroll',
      fileId: null,
      rowsJson: '[]'
    };
    this.bulkRowDrafts.set([]);
    this.bulkFileName.set('');
    this.bulkDialogVisible.set(true);
  }

  onBulkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.bulkForm.fileId = null;
      this.bulkFileName.set('');
      return;
    }
    this.bulkFileName.set(file.name);
    this.bulkForm.fileId = null;
  }

  openBulkRowDialog(index: number | null = null): void {
    this.bulkRowDialogIndex.set(index);
    if (index === null) {
      this.bulkRowDraft = emptyBulkRowDraft(this.bulkRowDrafts().length);
    } else {
      this.bulkRowDraft = { ...this.bulkRowDrafts()[index] };
    }
    this.bulkRowScalarPayloadJson = bulkRowScalarPayloadJson(this.bulkRowDraft);
    this.bulkRowDialogVisible.set(true);
  }

  saveBulkRowDialog(): void {
    const draft = applyBulkRowScalarPayload(this.bulkRowDraft, this.bulkRowScalarPayloadJson);
    const index = this.bulkRowDialogIndex();
    if (index === null) {
      this.bulkRowDrafts.set([...this.bulkRowDrafts(), draft]);
    } else {
      this.bulkRowDrafts.set(this.bulkRowDrafts().map((row, rowIndex) => rowIndex === index ? draft : row));
    }
    this.bulkRowDialogVisible.set(false);
  }

  removeBulkRow(index: number): void {
    this.bulkRowDrafts.set(this.bulkRowDrafts().filter((_, rowIndex) => rowIndex !== index));
  }

  saveCreate(): void {
    this.saving.set(true);
    this.error.set('');
    this.api.createMasterPolicy(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/master-policies', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveUpdate(): void {
    const item = this.item();
    if (!item) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.updateMasterPolicy(item.id, this.updateForm).subscribe({
      next: updated => {
        this.item.set(updated);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  deleteMasterPolicy(): void {
    const item = this.item();
    if (!item) {
      return;
    }

    if (!confirm(this.t('insurance.group.masterPolicyDetail.messages.deleteConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.deleteMasterPolicy(item.id).subscribe({
      next: () => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/master-policies']);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  addMember(): void {
    const item = this.item();
    if (!item) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.addMasterPolicyMember(item.id, this.memberForm).subscribe({
      next: created => {
        this.item.set({
          ...item,
          members: [created, ...item.members]
        });
        this.memberDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  createBulkBatch(): void {
    const item = this.item();
    if (!item) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    const payload: CreateInsuranceBulkChangeBatchPayload = {
      ...this.bulkForm,
      rowsJson: serializeBulkRowsJson(this.bulkRowDrafts())
    };
    this.api.createMasterPolicyBulkChange(item.id, payload).subscribe({
      next: batch => {
        this.item.set({
          ...item,
          bulkChanges: [batch, ...item.bulkChanges]
        });
        this.bulkDialogVisible.set(false);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  approveBulk(batchId: string): void {
    const item = this.item();
    if (!item) {
      return;
    }

    if (!confirm(this.t('insurance.group.masterPolicyDetail.messages.approveConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.approveMasterPolicyBulkChange(item.id, batchId).subscribe({
      next: updated => {
        this.item.set({
          ...item,
          bulkChanges: item.bulkChanges.map(row => row.id === updated.id ? updated : row)
        });
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  applyBulk(batchId: string): void {
    const item = this.item();
    if (!item) {
      return;
    }

    if (!confirm(this.t('insurance.group.masterPolicyDetail.messages.applyConfirm'))) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.api.applyMasterPolicyBulkChange(item.id, batchId).subscribe({
      next: updated => {
        this.item.set({
          ...item,
          bulkChanges: item.bulkChanges.map(row => row.id === updated.id ? updated : row)
        });
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  statusKey(status: string): string {
    return `insurance.masterPolicies.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  billingModeKey(mode: string): string {
    return `insurance.masterPolicies.billingMode.${mode.charAt(0).toLowerCase()}${mode.slice(1)}`;
  }

  bulkChangeTypeKey(changeType: string): string {
    return `insurance.masterPolicies.bulkChangeType.${changeType.charAt(0).toLowerCase()}${changeType.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}

function catchErrorToNull() {
  return (source: import('rxjs').Observable<InsuranceProductDetail | null>) =>
    source.pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
}
